import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { makePrioStyles } from '../../../../theme/utils';
import FilterContextVirtualTable from '../../../../components/Filter/FilterContextVirtualTable';
import { Column } from '@prio365/prio365-react-library/lib/VirtualTable/types/Column';
import FilterResultNoItemsScreen from '../../../../components/Filter/FilterResultNoItemsScreen';
import useFilterContext from '../../../../components/Filter/hooks/useFilterContext';
import {
  EmployeeOverviewCalculatedData,
  EmployeeOverviewSearchResultItem,
} from '../../../../models/TimeKeeping';
import { EmployeeContact } from '../../../../models/Employee';
import useExcelExport from '../../../../hooks/useExcelExport';
import { exportToCsv } from '../../../../util/export';

const useStyles = makePrioStyles((theme) => ({
  root: {},
  cell: {
    display: 'flex',
    alignItems: 'center',
  },
  cellVerticalAlign: {
    justifyContent: 'center',
  },
}));

interface HRTimeKeepingOverviewPageTableProps {
  className?: string;
}

export const HRTimeKeepingOverviewPageTable: React.FC<
  HRTimeKeepingOverviewPageTableProps
> = (props) => {
  //#region ------------------------------ Defaults
  const { className } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const { data, isLoading } = useFilterContext<
    EmployeeContact,
    EmployeeOverviewCalculatedData
  >();

  const tableData = useMemo(
    () =>
      data?.items?.sort((a, b) => {
        const lastNameComparison = a.data.lastName.localeCompare(
          b.data.lastName
        );
        if (lastNameComparison !== 0) {
          return lastNameComparison; // Sort by last name if they are not equal
        }
        return a.data.firstName.localeCompare(b.data.firstName); // Fallback to first name if last names are equal
      }) || [],
    [data?.items]
  );

  const [selectedSearchItems, setSelectedSearchItems] = useState<
    EmployeeOverviewSearchResultItem[]
  >([]);

  const exportExcel = useExcelExport();
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const formatNullableDays = (value: number | null | undefined) =>
    value === null || value === undefined
      ? '-'
      : `${value.toLocaleString()}  ${t(
          `absences:absenceOverview.popOver.${value === 1 ? 'day' : 'days'}`
        )}`;

  const formatNullableHours = (value: number | null | undefined) =>
    value === null || value === undefined ? '-' : `${value.toLocaleString()} h`;

  const formatNullableDecimalHours = (value: number | null | undefined) => {
    if (value === null || value === undefined) {
      return '-';
    }
    return `${
      value.toString().includes('.')
        ? value.toFixed(2).replace('.', ',')
        : value || '0'
    } h`;
  };

  const formatNullableNumber = (value: number | null | undefined) =>
    value === null || value === undefined ? '-' : value.toLocaleString();

  const onSelectionChange = (
    selectedItems: EmployeeOverviewSearchResultItem[]
  ) => {
    setSelectedSearchItems(selectedItems);
  };

  const handleOnExportToExcelClick = () => {
    const items =
      selectedSearchItems.length > 0 ? selectedSearchItems : data?.items;
    const overviewItems = items.map((item) => {
      return {
        [t('hr:timeAndLeaveManagement.overview.employee')]:
          `${item.data.firstName} ${item.data.lastName}`,
        [`${t('hr:timeAndLeaveManagement.overview.holidayEntitlements')} (${t(`absences:absenceOverview.popOver.days`)})`]: formatNullableNumber(item.calculated.holidayEntitlements),
        [`${t('hr:timeAndLeaveManagement.overview.remainingLeave')} (${t(`absences:absenceOverview.popOver.days`)})`]: formatNullableNumber(item.calculated.remainingLeave),
        [`${t('hr:timeAndLeaveManagement.overview.remainingLeaveLastYear', {
          lastYear: new Date().getFullYear() - 1,
        })} (${t(`absences:absenceOverview.popOver.days`)})`]:
          formatNullableNumber(item.calculated.remainingLeaveLastYear),
        [`${t('hr:timeAndLeaveManagement.overview.paidOvertime')} (h)`]: formatNullableNumber(item.calculated.paidOvertime),
        [`${t('hr:timeAndLeaveManagement.overview.currentOvertime')} (h)`]: formatNullableNumber(item.calculated.currentOvertime),
        [`${t('hr:timeAndLeaveManagement.overview.unsettledOvertimeHours')} (h)`]: formatNullableNumber(item.calculated.unsettledOvertimeHours),
        [`${t('hr:timeAndLeaveManagement.overview.sickDaysCurrentYear', {
          currentYear: new Date().getFullYear(),
        })} (${t(`absences:absenceOverview.popOver.days`)})`]:
          formatNullableNumber(item.calculated.sickDaysCurrentYear),
        [`${t(
          'hr:timeAndLeaveManagement.overview.overtimeCompensationCurrentYear',
          {
            currentYear: new Date().getFullYear(),
          }
        )} (${t(`absences:absenceOverview.popOver.days`)})`]:
          formatNullableNumber(item.calculated.overtimeCompensationCurrentYear),
        [`${t('hr:timeAndLeaveManagement.overview.approvedLeaveNextYear', {
          nextYear: new Date().getFullYear() + 1,
        })} (${t(`absences:absenceOverview.popOver.days`)})`]:
          formatNullableNumber(item.calculated.approvedLeaveNextYear),
        [`${t(
          'hr:timeAndLeaveManagement.overview.overtimeCompensationNextYear',
          {
            nextYear: new Date().getFullYear() + 1,
          }
        )} (${t(`absences:absenceOverview.popOver.days`)})`]:
          formatNullableNumber(item.calculated.overtimeCompensationNextYear),
      };
    });

    exportExcel(
      overviewItems,
      t('hr:timeAndLeaveManagement.overview.export.title')
    );
  };

  const handleOnExportToCsvClick = () => {
    const items =
      selectedSearchItems.length > 0 ? selectedSearchItems : data?.items;

    const headers = [
      t('hr:timeAndLeaveManagement.overview.employee'),
      `${t('hr:timeAndLeaveManagement.overview.holidayEntitlements')} (${t(
        `absences:absenceOverview.popOver.days`
      )})`,
      `${t('hr:timeAndLeaveManagement.overview.remainingLeave')} (${t(
        `absences:absenceOverview.popOver.days`
      )})`,
      `${t('hr:timeAndLeaveManagement.overview.remainingLeaveLastYear', {
        lastYear: new Date().getFullYear() - 1,
      })} (${t(`absences:absenceOverview.popOver.days`)})`,
      `${t('hr:timeAndLeaveManagement.overview.paidOvertime')} (h)`,
      `${t('hr:timeAndLeaveManagement.overview.currentOvertime')} (h)`,
      `${t('hr:timeAndLeaveManagement.overview.unsettledOvertimeHours')} (h)`,
      `${t('hr:timeAndLeaveManagement.overview.sickDaysCurrentYear', {
        currentYear: new Date().getFullYear(),
      })} (${t(`absences:absenceOverview.popOver.days`)})`,
      `${t(
        'hr:timeAndLeaveManagement.overview.overtimeCompensationCurrentYear',
        {
          currentYear: new Date().getFullYear(),
        }
      )} (${t(`absences:absenceOverview.popOver.days`)})`,
      `${t('hr:timeAndLeaveManagement.overview.approvedLeaveNextYear', {
        nextYear: new Date().getFullYear() + 1,
      })} (${t(`absences:absenceOverview.popOver.days`)})`,
      `${t('hr:timeAndLeaveManagement.overview.overtimeCompensationNextYear', {
        nextYear: new Date().getFullYear() + 1,
      })} (${t(`absences:absenceOverview.popOver.days`)})`,
    ];
    const csvData = items.map((item) => {
      return [
        `${item.data.firstName} ${item.data.lastName}`,
        formatNullableNumber(item.calculated.holidayEntitlements),
        formatNullableNumber(item.calculated.remainingLeave),
        formatNullableNumber(item.calculated.remainingLeaveLastYear),
        formatNullableNumber(item.calculated.paidOvertime),
        formatNullableNumber(item.calculated.currentOvertime),
        formatNullableNumber(item.calculated.unsettledOvertimeHours),
        formatNullableNumber(item.calculated.sickDaysCurrentYear),
        formatNullableNumber(item.calculated.overtimeCompensationCurrentYear),
        formatNullableNumber(item.calculated.approvedLeaveNextYear),
        formatNullableNumber(item.calculated.overtimeCompensationNextYear),
      ];
    });

    exportToCsv(
      headers,
      csvData,
      t('hr:timeAndLeaveManagement.overview.export.title')
    );
  };
  //#endregion

  //#region ------------------------------ Effects
  //#endregion

  //#region ------------------------------ Columns
  const columns: Column<EmployeeOverviewSearchResultItem>[] = [
    {
      id: 'employee',
      width: 200,
      title: t('hr:timeAndLeaveManagement.overview.employee'),
      accessor: 'data.employeeContactId',
      sortingFn: (rowA, rowB) => {
        const contactNameA = `${rowA.data.firstName} ${rowA.data.lastName}`;
        const contactNameB = `${rowB.data.firstName} ${rowB.data.lastName}`;
        return contactNameA.localeCompare(contactNameB);
      },
      Cell: ({
        originalData: {
          data: { firstName, lastName },
        },
      }) => {
        return `${firstName} ${lastName}`;
      },
      alignSelf: true,
    },
    {
      id: 'holidayEntitlements',
      width: 150,
      title: t('hr:timeAndLeaveManagement.overview.holidayEntitlements'),
      accessor: 'calculated.holidayEntitlements',
      sortingFn: (rowA, rowB) =>
        rowA.calculated.holidayEntitlements -
        rowB.calculated.holidayEntitlements,
      Cell: ({
        originalData: {
          calculated: { holidayEntitlements },
        },
      }) => <div>{formatNullableDays(holidayEntitlements)}</div>,
      className: classNames(classes.cell, classes.cellVerticalAlign),
      alignSelf: true,
    },
    {
      id: 'remainingLeave',
      width: 120,
      title: t('hr:timeAndLeaveManagement.overview.remainingLeave'),
      accessor: 'calculated.remainingLeave',
      sortingFn: (rowA, rowB) =>
        rowA.calculated.remainingLeave - rowB.calculated.remainingLeave,
      Cell: ({
        originalData: {
          calculated: { remainingLeave },
        },
      }) => <div> {formatNullableDays(remainingLeave)}</div>,
      className: classNames(classes.cell, classes.cellVerticalAlign),
      alignSelf: true,
    },
    {
      id: 'remainingLeaveLastYear',
      width: 150,
      title: t('hr:timeAndLeaveManagement.overview.remainingLeaveLastYear', {
        lastYear: new Date().getFullYear() - 1,
      }),
      accessor: 'calculated.remainingLeaveLastYear',
      sortingFn: (rowA, rowB) =>
        rowA.calculated.remainingLeaveLastYear -
        rowB.calculated.remainingLeaveLastYear,
      Cell: ({
        originalData: {
          calculated: { remainingLeaveLastYear },
        },
      }) => <div>{formatNullableDays(remainingLeaveLastYear)}</div>,
      className: classNames(classes.cell, classes.cellVerticalAlign),
      alignSelf: true,
    },
    {
      id: 'paidOvertime',
      width: 200,
      title: t('hr:timeAndLeaveManagement.overview.paidOvertime'),
      accessor: 'calculated.paidOvertime',
      sortingFn: (rowA, rowB) =>
        rowA.calculated.paidOvertime - rowB.calculated.paidOvertime,
      Cell: ({
        originalData: {
          calculated: { paidOvertime },
        },
      }) => <div> {formatNullableHours(paidOvertime)}</div>,
      className: classNames(classes.cell, classes.cellVerticalAlign),
      alignSelf: true,
    },
    {
      id: 'currentOvertime',
      width: 200,
      title: t('hr:timeAndLeaveManagement.overview.currentOvertime'),
      accessor: 'calculated.currentOvertime',
      sortingFn: (rowA, rowB) =>
        rowA.calculated.currentOvertime - rowB.calculated.currentOvertime,
      Cell: ({
        originalData: {
          calculated: { currentOvertime },
        },
      }) => (
        // parse to two decimal places but with , and only if there are some decimal
        <div>{formatNullableDecimalHours(currentOvertime)}</div>
      ),
      className: classNames(classes.cell, classes.cellVerticalAlign),
      alignSelf: true,
    },
    {
      id: 'unsettledOvertimeHours',
      width: 180,
      title: t('hr:timeAndLeaveManagement.overview.unsettledOvertimeHours'),
      accessor: 'calculated.unsettledOvertimeHours',
      sortingFn: (rowA, rowB) =>
        rowA.calculated.unsettledOvertimeHours -
        rowB.calculated.unsettledOvertimeHours,
      Cell: ({
        originalData: {
          calculated: { unsettledOvertimeHours },
        },
      }) => (
        // parse to two decimal places but with , and only if there are some decimal
        <div>{formatNullableDecimalHours(unsettledOvertimeHours)}</div>
      ),
      className: classNames(classes.cell, classes.cellVerticalAlign),
      alignSelf: true,
    },
    {
      id: 'sickDaysCurrentYear',
      width: 150,
      title: t('hr:timeAndLeaveManagement.overview.sickDaysCurrentYear', {
        currentYear: new Date().getFullYear(),
      }),
      accessor: 'calculated.sickDaysCurrentYear',
      sortingFn: (rowA, rowB) =>
        rowA.calculated.sickDaysCurrentYear -
        rowB.calculated.sickDaysCurrentYear,
      Cell: ({
        originalData: {
          calculated: { sickDaysCurrentYear },
        },
      }) => <div> {formatNullableDays(sickDaysCurrentYear)}</div>,
      className: classNames(classes.cell, classes.cellVerticalAlign),
      alignSelf: true,
    },
    {
      id: 'overtimeCompensationCurrentYear',
      width: 150,
      title: t(
        'hr:timeAndLeaveManagement.overview.overtimeCompensationCurrentYear',
        {
          currentYear: new Date().getFullYear(),
        }
      ),
      accessor: 'calculated.overtimeCompensationCurrentYear',
      sortingFn: (rowA, rowB) =>
        rowA.calculated.overtimeCompensationCurrentYear -
        rowB.calculated.overtimeCompensationCurrentYear,
      Cell: ({
        originalData: {
          calculated: { overtimeCompensationCurrentYear },
        },
      }) => <div> {formatNullableDays(overtimeCompensationCurrentYear)}</div>,
      className: classNames(classes.cell, classes.cellVerticalAlign),
      alignSelf: true,
    },
    {
      id: 'approvedLeaveNextYear',
      width: 150,
      title: t('hr:timeAndLeaveManagement.overview.approvedLeaveNextYear', {
        nextYear: new Date().getFullYear() + 1,
      }),
      accessor: 'calculated.approvedLeaveNextYear',
      sortingFn: (rowA, rowB) =>
        rowA.calculated.approvedLeaveNextYear -
        rowB.calculated.approvedLeaveNextYear,
      Cell: ({
        originalData: {
          calculated: { approvedLeaveNextYear },
        },
      }) => <div>{formatNullableDays(approvedLeaveNextYear)}</div>,
      className: classNames(classes.cell, classes.cellVerticalAlign),
      alignSelf: true,
    },
    {
      id: 'overtimeCompensationNextYear',
      width: 150,
      title: t(
        'hr:timeAndLeaveManagement.overview.overtimeCompensationNextYear',
        {
          nextYear: new Date().getFullYear() + 1,
        }
      ),
      accessor: 'calculated.overtimeCompensationNextYear',
      sortingFn: (rowA, rowB) =>
        rowA.calculated.overtimeCompensationNextYear -
        rowB.calculated.overtimeCompensationNextYear,
      Cell: ({
        originalData: {
          calculated: { overtimeCompensationNextYear },
        },
      }) => <div> {formatNullableDays(overtimeCompensationNextYear)}</div>,
      className: classNames(classes.cell, classes.cellVerticalAlign),
      alignSelf: true,
    },
  ];
  //#endregion

  return (
    <FilterContextVirtualTable<EmployeeOverviewSearchResultItem>
      id={'timekeepingOverview'}
      className={classNames(classes.root, className)}
      columns={columns}
      data={tableData}
      selectedItems={selectedSearchItems}
      resizable="absolute"
      onSelectionChange={onSelectionChange}
      onCheckEquality={(a, b) =>
        a.data.employeeContactId === b.data.employeeContactId
      }
      noItemsScreen={<FilterResultNoItemsScreen />}
      loading={
        isLoading && {
          type: 'noItems',
        }
      }
      rowsAreSelectable
      actionBarDefaultButtons={
        data?.totalItems > 0
          ? [
              {
                children: t('common:actions.csvExport'),
                iconProp: 'file-arrow-down',
                onClick: handleOnExportToCsvClick,
              },
              {
                children: t('common:actions.excelExport'),
                iconProp: 'file-excel',
                onClick: handleOnExportToExcelClick,
              },
            ]
          : []
      }
      actionBarButtons={[
        {
          children: t('common:actions.excelExportSelection'),
          iconProp: 'file-excel',
          onClick: handleOnExportToExcelClick,
        },
        {
          children: t('common:actions.csvExportSelection'),
          iconProp: 'file-arrow-down',
          onClick: handleOnExportToCsvClick,
        },
      ]}
    />
  );
};

export default HRTimeKeepingOverviewPageTable;
